import {
  createTheme,
  responsiveFontSizes,
  type ThemeOptions,
  type Theme,
  type CssVarsThemeOptions,
} from '@mui/material';

const commonThemeOptions: ThemeOptions &
  Pick<CssVarsThemeOptions, 'defaultColorScheme' | 'colorSchemes' | 'components'> = {
  typography: {
    fontFamily: 'whitneybook, Arial, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightBold: 700,
    h1: {
      fontWeight: 800,
      fontSize: 64,
      lineHeight: 1.25,
      fontFamily: 'futura, Impact, Haettenschweiler, Arial Narrow Bold, sans-serif',
    },
    h2: {
      fontWeight: 800,
      fontSize: 48,
      lineHeight: 1.25,
      fontFamily: 'futura, Impact, Haettenschweiler, Arial Narrow Bold, sans-serif',
    },
    h3: {
      fontSize: 32,
      fontWeight: 800,
      lineHeight: 1.25,
      fontFamily: 'futura, Impact, Haettenschweiler, Arial Narrow Bold, sans-serif',
    },
    h4: {
      fontSize: 30,
      fontWeight: 500,
      lineHeight: 1.2,
      fontFamily: 'whitneybook, Arial, sans-serif',
    },
    h5: {
      fontWeight: 800,
      fontSize: 24,
      lineHeight: 1.25,
      fontFamily: 'futura, Impact, Haettenschweiler, Arial Narrow Bold, sans-serif',
    },
    h6: {
      fontWeight: 800,
      fontSize: 18,
      lineHeight: 1.25,
      fontFamily: 'futura, Impact, Haettenschweiler, Arial Narrow Bold, sans-serif',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: "whitneybook";
          src: url("https://graphdev.morganlewis.net/contents/fonts/Whitney/whitney-book-bas-webfont.eot?#iefix") format("embedded-opentype"),
               url("https://graphdev.morganlewis.net/contents/fonts/Whitney/whitney-book-bas-webfont.woff2") format("woff2"),
               url("https://graphdev.morganlewis.net/contents/fonts/Whitney/whitney-book-bas-webfont.woff") format("woff"),
               url("https://graphdev.morganlewis.net/contents/fonts/Whitney/whitney-book-bas-webfont.ttf") format("truetype"),
               url("https://graphdev.morganlewis.net/contents/fonts/Whitney/whitney-book-bas-webfont.svg#whitneybook") format("svg");
        }
        @font-face {
          font-family: "futura";
          src: url("https://graphdev.morganlewis.net/contents/fonts/FuturaW01-ExtraBoldCond/bec66ca3-753a-469a-b153-fdd9e3ea7ce4.eot?#iefix") format("embedded-opentype"),
               url("https://graphdev.morganlewis.net/contents/fonts/FuturaW01-ExtraBoldCond/04733668-9f3f-4f2f-a534-14fb44683f83.woff") format("woff"),
               url("https://graphdev.morganlewis.net/contents/fonts/FuturaW01-ExtraBoldCond/1f9bced0-1224-4785-9553-68fcbac00914.ttf") format("truetype"),
               url("https://graphdev.morganlewis.net/contents/fonts/FuturaW01-ExtraBoldCond/23d05127-305c-4753-8f3a-16d8109db5ca.svg#23d05127-305c-4753-8f3a-16d8109db5ca") format("svg");
        }
      `,
    },
    MuiAppBar: {
      styleOverrides: {
        colorInherit: {
          backgroundColor: '#2e2d2e',
          color: '#fff',
        },
      },
      defaultProps: {
        color: 'inherit',
      },
    },
  },
};

// Light theme
const lightThemeOptions: ThemeOptions = {
  ...commonThemeOptions,
  palette: {
    mode: 'light',
    primary: {
      main: '#4f3996',
      light: '#dcd7e9',
      dark: '#4f3996',
    },
    secondary: {
      main: '#93d8f7',
      light: '#d3effb',
      dark: '#65c0e0',
    },
    error: {
      main: '#e61432',
      light: '#f5a1ad',
      dark: '#b60000',
    },
    warning: {
      main: '#ff8b2a',
      dark: '#e17b25',
      light: '#ffd0a9',
    },
    info: {
      main: '#d3effb',
      dark: '#65c0e0',
    },
    success: {
      main: '#007836',
      light: '#98c8ae',
      dark: '#01672f',
    },
  },
  components: {
    ...commonThemeOptions.components,
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f5f5',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f5f5',
        },
      },
    },
  },
};

// Dark theme
const darkThemeOptions: ThemeOptions = {
  ...commonThemeOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: '#4f3996',
      light: '#dcd7e9',
      dark: '#4f3996',
    },
    secondary: {
      main: '#93d8f7',
      light: '#d3effb',
      dark: '#65c0e0',
    },
    error: {
      main: '#e61432',
      light: '#f5a1ad',
      dark: '#b60000',
    },
    warning: {
      main: '#ff8b2a',
      dark: '#e17b25',
      light: '#ffd0a9',
    },
    info: {
      main: '#d3effb',
      dark: '#65c0e0',
    },
    success: {
      main: '#007836',
      light: '#98c8ae',
      dark: '#01672f',
    },
  },
  components: {
    ...commonThemeOptions.components,
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2e2d2e',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          backgroundColor: '#2e2d2e',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          backgroundColor: '#2e2d2e',
        },
      },
    },
  },
};

export const appLightTheme: Theme = responsiveFontSizes(createTheme(lightThemeOptions));
export const appDarkTheme: Theme = responsiveFontSizes(createTheme(darkThemeOptions));
